/**
 * Manages the microphone audio processing pipeline.
 *
 * Responsibilities:
 * - Enumerate and select audio input devices
 * - Set up Web Audio chain: source → raw analyser → compressor → gain → boosted analyser → destination
 * - Drive volume meters (raw and boosted) at 100ms intervals
 * - Mic boost toggle (DynamicsCompressor settings, localStorage-persisted)
 * - Wake lock to prevent screen sleep during recording
 * - Elapsed recording timer
 * - getMimeType for MediaRecorder format selection
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { pickRecorderMimeType } from "../lib/recorder-mime"

interface IWakeLockSentinel {
  released: boolean
  release: () => Promise<void>
}

/**
 * Opus bitrate for MediaRecorder. The browser default for webm/opus is often as low
 * as ~32–40 kbps, which bakes audible compression artefacts into the audio before it
 * ever reaches the STT engine. 128 kbps is transparent for speech and sits well above
 * the lossy-format quality threshold transcription providers recommend (~128 kbps+).
 */
export const RECORDER_AUDIO_BITS_PER_SECOND = 128_000

/**
 * Boost stage tuned for hybrid meetings (mixed near/far speakers around one mic).
 *
 * The previous curve was effectively a brick-wall limiter (−50 dB threshold, 12:1,
 * 2× makeup) that crushed dynamics and pulled the noise floor / room reverb up with
 * the signal — which hurts diarisation (speaker separation leans on relative level)
 * and amplifies remote-audio bleed in a hybrid room. This is a gentle leveller
 * instead: it lifts quiet, far speakers without flattening the conversation or
 * pumping up background noise.
 */
function applyBoostSettings(compressor: DynamicsCompressorNode, gain: GainNode, enabled: boolean): void {
  if (enabled) {
    compressor.threshold.value = -24
    compressor.knee.value = 30
    compressor.ratio.value = 3
    compressor.attack.value = 0.003
    compressor.release.value = 0.25
    gain.gain.value = 1.5
  } else {
    compressor.threshold.value = 0
    compressor.knee.value = 0
    compressor.ratio.value = 1
    compressor.attack.value = 0.003
    compressor.release.value = 0.25
    gain.gain.value = 1.0
  }
}

/**
 * Number of frequency bands the live visualiser draws. The hook collapses each
 * raw FFT frame into exactly this many bands so the meter can render real
 * per-frequency energy instead of a canned wave.
 */
export const SPECTRUM_BAND_COUNT = 48

/**
 * Collapse one FFT magnitude frame (`getByteFrequencyData`, 0..255 per bin)
 * into `bandCount` bands for the live visualiser.
 *
 * Bins are grouped on a LOG-frequency axis: speech energy crowds the low end,
 * so a linear bin→bar map would leave the right half of the meter permanently
 * dead. We skip bin 0 (DC/rumble) and cap the top at the lower half of the
 * spectrum (~12 kHz at a 48 kHz rate), above which speech carries almost
 * nothing.
 *
 * Each source bin is assigned to exactly one band by its log-frequency
 * position and the band keeps the PEAK of the bins it owns — so every bin
 * contributes (nothing dropped at the edges) and a narrow formant or sibilant
 * still registers. Bands too fine to own any bin (the very low end, where
 * several bars fall between two bins) are then filled by linear interpolation
 * from their nearest owning neighbours, so the low bars form a smooth ramp
 * rather than a block of identical bars.
 *
 * Output is 0..1 per band — the visualiser owns the floor, response curve and colour.
 */
export function buildSpectrumBands(freq: Uint8Array, bandCount: number): number[] {
  const bins = freq.length
  if (bins < 2) return new Array<number>(bandCount).fill(0)
  const minBin = 1
  const maxBin = Math.max(minBin + 1, Math.floor(bins / 2))
  const logMin = Math.log(minBin)
  const logSpan = Math.log(maxBin) - logMin

  // Per-band peak; -1 marks a band that no source bin landed in.
  const raw = new Array<number>(bandCount).fill(-1)
  for (let b = minBin; b <= maxBin && b < bins; b++) {
    let band = Math.floor((bandCount * (Math.log(b) - logMin)) / logSpan)
    if (band < 0) band = 0
    if (band >= bandCount) band = bandCount - 1
    const v = freq[b] / 255
    if (v > raw[band]) raw[band] = v
  }

  // Fill the sub-bin gaps by interpolating between the nearest owned bands.
  const out = new Array<number>(bandCount)
  for (let i = 0; i < bandCount; i++) {
    if (raw[i] >= 0) {
      out[i] = raw[i]
      continue
    }
    let lo = i - 1
    while (lo >= 0 && raw[lo] < 0) lo--
    let hi = i + 1
    while (hi < bandCount && raw[hi] < 0) hi++
    const loVal = lo >= 0 ? raw[lo] : hi < bandCount ? raw[hi] : 0
    const hiVal = hi < bandCount ? raw[hi] : loVal
    out[i] = hi > lo ? loVal + ((hiVal - loVal) * (i - lo)) / (hi - lo) : loVal
  }
  return out
}

/**
 * getUserMedia constraints for mic capture. Capture as cleanly as possible and let the
 * STT engine's own front-end do the rest — all three browser DSP stages are off:
 *
 * - echoCancellation OFF: in the desktop WebView (WKWebView on macOS / WebView2 on
 *   Windows) enabling it grabs the OS voice-processing path — the macOS Voice-Processing
 *   I/O audio unit, or Windows "communications" signal processing — on the shared mic.
 *   Any other softphone using that same path (Microsoft Teams, a WebRTC app) then loses
 *   its mic and the remote participants hear silence. Zoom uses its own AEC and is
 *   unaffected, which is why only Teams broke. Trade-off: we lose speaker→mic bleed
 *   suppression in hybrid rooms; revisit with a conditional (only enable when no remote
 *   process-tap channel is active) or a user toggle.
 * - noiseSuppression / AGC OFF: both are aggressive, non-linear processors that strip
 *   speaker-distinguishing cues and pump up the noise floor — modern ASR / diarisation
 *   models normalise internally and prefer the unprocessed signal.
 *
 * The device is requested with `ideal`, never `exact`: device IDs rotate across sessions
 * and reconnects (Bluetooth, docks, USB re-plug), and `exact` would reject with an
 * OverconstrainedError and acquire *nothing* rather than falling back to the default mic.
 */
function buildAudioConstraints(deviceId: string): MediaTrackConstraints {
  return {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
    ...(deviceId !== "default" && { deviceId: { ideal: deviceId } }),
  }
}

/** Map a getUserMedia / device rejection to an honest, actionable message. */
function micErrorMessage(err: unknown): string {
  const name = err instanceof Error ? err.name : ""
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Microphone access denied. Please allow microphone access and try again."
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No microphone found. Connect a microphone and try again."
    case "NotReadableError":
    case "TrackStartError":
      return "Your microphone is in use by another app. Close it (for example a call app) and try again."
    case "OverconstrainedError":
    case "ConstraintNotSatisfiedError":
      return "The selected microphone is unavailable. Pick another in the list and try again."
    default:
      return "Could not start the microphone. Please check it and try again."
  }
}

interface IProps {
  setError: (err: string) => void
}

interface IUseAudioPipelineResult {
  streamRef: React.MutableRefObject<MediaStream | null>
  audioCtxRef: React.MutableRefObject<AudioContext | null>
  destinationRef: React.MutableRefObject<MediaStreamAudioDestinationNode | null>
  currentVolumeRef: React.MutableRefObject<number>
  startTimeRef: React.MutableRefObject<number>
  pausedElapsedRef: React.MutableRefObject<number>
  monitoring: boolean
  devices: MediaDeviceInfo[]
  selectedDevice: string | null
  setSelectedDevice: React.Dispatch<React.SetStateAction<string | null>>
  elapsed: number
  setElapsed: React.Dispatch<React.SetStateAction<number>>
  volume: number
  /** Per-frequency band magnitudes (0..1) for the live visualiser; empty until monitoring starts. */
  spectrum: number[]
  boostedVolume: number
  boostEnabled: boolean
  startMonitoring: (deviceId: string) => Promise<void>
  requestWakeLock: () => Promise<void>
  releaseWakeLock: () => void
  startElapsedTimer: () => void
  stopElapsedTimer: () => void
  getMimeType: () => string | null
  handleBoostToggle: (enabled: boolean) => void
}

export function useAudioPipeline({ setError }: IProps): IUseAudioPipelineResult {
  // Audio pipeline refs — internal to this hook
  const streamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const compressorRef = useRef<DynamicsCompressorNode | null>(null)
  const gainNodeRef = useRef<GainNode | null>(null)
  const boostedAnalyserRef = useRef<AnalyserNode | null>(null)
  const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null)
  const boostEnabledRef = useRef(true)
  const currentVolumeRef = useRef(0)
  const animFrameRef = useRef<number>(0)
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef(0)
  const pausedElapsedRef = useRef(0)
  const wakeLockRef = useRef<IWakeLockSentinel | null>(null)
  // Mic-loss recovery + interruption-resume state. `recoverActiveRef` gates both: true
  // only while a live monitoring session exists (false during teardown), so neither the
  // statechange auto-resume nor the track-loss reacquire fires while we're tearing down.
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const lastDeviceIdRef = useRef<string>("default")
  const recoverActiveRef = useRef(false)
  const reacquiringRef = useRef(false)
  const reacquireRef = useRef<() => void>(() => {})

  const [monitoring, setMonitoring] = useState(false)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [volume, setVolume] = useState(0)
  const [spectrum, setSpectrum] = useState<number[]>([])
  const [boostedVolume, setBoostedVolume] = useState(0)
  const [boostEnabled, setBoostEnabled] = useState(() => {
    const stored = localStorage.getItem("lexicanon:boost-enabled")
    return stored !== null ? stored === "true" : true
  })

  // Keep ref in sync for use inside setInterval closures
  boostEnabledRef.current = boostEnabled

  // Enumerate devices on mount
  useEffect(() => {
    ;(async () => {
      try {
        const devs = await navigator.mediaDevices.enumerateDevices()
        const audioInputs = devs.filter((d) => d.kind === "audioinput" && d.deviceId)
        if (audioInputs.length > 0 && audioInputs[0].label) {
          setDevices(audioInputs)
          setSelectedDevice(audioInputs[0].deviceId)
        } else {
          setDevices(
            audioInputs.length > 0
              ? audioInputs
              : [
                  {
                    deviceId: "default",
                    kind: "audioinput",
                    label: "Default Microphone",
                    groupId: "",
                    toJSON: () => ({}),
                  } as MediaDeviceInfo,
                ],
          )
          setSelectedDevice(audioInputs[0]?.deviceId || "default")
        }
      } catch {
        setError("Could not enumerate audio devices.")
      }
    })()
  }, [setError])

  /** Re-arm involuntary-loss recovery on a stream's audio tracks. `stop()` never fires
   *  `ended`, so an `ended` event means the device vanished (unplug, BT drop, OS revoke). */
  const attachTrackRecovery = useCallback((stream: MediaStream): void => {
    for (const track of stream.getAudioTracks()) {
      track.onended = (): void => {
        if (recoverActiveRef.current) reacquireRef.current()
      }
    }
  }, [])

  /**
   * Hot-swap a fresh mic into the EXISTING graph after the input device is lost.
   *
   * Critically this reuses the same AudioContext and MediaStreamDestination: the
   * MediaRecorder is wired to that destination's stream, so reconnecting only the
   * source→analyser/compressor edge keeps the recording going without a gap. Rebuilding
   * the context (as a fresh startMonitoring would) would leave the recorder bound to the
   * dead destination — i.e. recording silence.
   */
  const reacquireMic = useCallback(async (): Promise<void> => {
    const ctx = audioCtxRef.current
    const compressor = compressorRef.current
    const rawAnalyser = analyserRef.current
    if (!ctx || !compressor || !rawAnalyser || ctx.state === "closed") return
    if (reacquiringRef.current) return
    reacquiringRef.current = true
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: buildAudioConstraints(lastDeviceIdRef.current),
      })
      streamRef.current?.getTracks().forEach((t) => {
        t.stop()
      })
      sourceRef.current?.disconnect()
      streamRef.current = stream
      const source = ctx.createMediaStreamSource(stream)
      source.connect(rawAnalyser)
      source.connect(compressor)
      sourceRef.current = source
      attachTrackRecovery(stream)
      if (ctx.state !== "running") await ctx.resume()
      const activeId = stream.getAudioTracks()[0]?.getSettings()?.deviceId
      if (activeId) setSelectedDevice(activeId)
      setError("")
    } catch (err) {
      setError(micErrorMessage(err))
    } finally {
      reacquiringRef.current = false
    }
  }, [attachTrackRecovery, setError])

  // Stable indirection so track/devicechange handlers always call the latest reacquire.
  reacquireRef.current = (): void => {
    void reacquireMic()
  }

  const startMonitoring = useCallback(
    async (deviceId: string): Promise<void> => {
      recoverActiveRef.current = false
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => {
          t.stop()
        })
      }
      sourceRef.current?.disconnect()
      sourceRef.current = null
      if (audioCtxRef.current) {
        audioCtxRef.current.onstatechange = null
        audioCtxRef.current.close()
      }
      clearInterval(animFrameRef.current)
      try {
        lastDeviceIdRef.current = deviceId
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: buildAudioConstraints(deviceId),
        })
        streamRef.current = stream
        attachTrackRecovery(stream)

        const devs = await navigator.mediaDevices.enumerateDevices()
        const audioInputs = devs.filter((d) => d.kind === "audioinput" && d.deviceId)
        if (audioInputs.length > 0) {
          setDevices(audioInputs)
          const activeId = stream.getAudioTracks()[0]?.getSettings()?.deviceId
          if (activeId && audioInputs.some((d) => d.deviceId === activeId)) {
            setSelectedDevice(activeId)
          }
        }

        const ctx = new AudioContext()
        if (ctx.state === "suspended") await ctx.resume()
        ctx.onstatechange = (): void => {
          // WebKit suspends (or `interrupted`s) the context on audio-session interruptions
          // — Siri, an incoming call, sleep/wake. We record the processed graph output, not
          // the raw track, so a suspended context bakes in silence. Re-resume while live.
          const state: string = ctx.state
          if (recoverActiveRef.current && state !== "running" && state !== "closed") {
            void ctx.resume().catch(() => {})
          }
        }
        const source = ctx.createMediaStreamSource(stream)

        const rawAnalyser = ctx.createAnalyser()
        rawAnalyser.fftSize = 256
        source.connect(rawAnalyser)

        const compressor = ctx.createDynamicsCompressor()
        const gainNode = ctx.createGain()
        const boostedAnalyser = ctx.createAnalyser()
        boostedAnalyser.fftSize = 256
        const destination = ctx.createMediaStreamDestination()

        source.connect(compressor)
        compressor.connect(gainNode)
        gainNode.connect(boostedAnalyser)
        boostedAnalyser.connect(destination)

        applyBoostSettings(compressor, gainNode, boostEnabledRef.current)

        audioCtxRef.current = ctx
        sourceRef.current = source
        analyserRef.current = rawAnalyser
        compressorRef.current = compressor
        gainNodeRef.current = gainNode
        boostedAnalyserRef.current = boostedAnalyser
        destinationRef.current = destination

        const rawData = new Uint8Array(rawAnalyser.frequencyBinCount)
        const boostedData = new Uint8Array(boostedAnalyser.frequencyBinCount)
        const volumeInterval = setInterval(() => {
          rawAnalyser.getByteFrequencyData(rawData)
          const rawVol = rawData.reduce((a, b) => a + b, 0) / rawData.length / 255
          currentVolumeRef.current = rawVol
          setVolume(rawVol)
          // Same frame, two readings: the scalar above drives level/clipping
          // logic; the bands below drive the real per-frequency visualiser.
          setSpectrum(buildSpectrumBands(rawData, SPECTRUM_BAND_COUNT))
          boostedAnalyser.getByteFrequencyData(boostedData)
          setBoostedVolume(boostedData.reduce((a, b) => a + b, 0) / boostedData.length / 255)
        }, 100)
        animFrameRef.current = volumeInterval as unknown as number
        setMonitoring(true)
        recoverActiveRef.current = true
        setError("")
      } catch (err) {
        setError(micErrorMessage(err))
        setMonitoring(false)
      }
    },
    [setError, attachTrackRecovery],
  )

  // Refresh the device list on hot-plug, and recover if our input died without firing
  // `ended` (some platforms mute the track instead of ending it on device removal).
  useEffect(() => {
    const md = navigator.mediaDevices
    if (!md?.addEventListener) return
    const handler = (): void => {
      void (async () => {
        try {
          const devs = await md.enumerateDevices()
          const audioInputs = devs.filter((d) => d.kind === "audioinput" && d.deviceId)
          if (audioInputs.length > 0) setDevices(audioInputs)
        } catch {
          // best-effort list refresh
        }
        const track = streamRef.current?.getAudioTracks()[0]
        if (recoverActiveRef.current && (!track || track.readyState === "ended")) {
          reacquireRef.current()
        }
      })()
    }
    md.addEventListener("devicechange", handler)
    return () => {
      md.removeEventListener("devicechange", handler)
    }
  }, [])

  const requestWakeLock = useCallback(async (): Promise<void> => {
    if (!("wakeLock" in navigator)) return
    try {
      const nav = navigator as Navigator & {
        wakeLock: { request: (type: "screen") => Promise<IWakeLockSentinel> }
      }
      wakeLockRef.current = await nav.wakeLock.request("screen")
    } catch {
      // Wake lock not critical — recording continues without it
    }
  }, [])

  const releaseWakeLock = useCallback((): void => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {})
      wakeLockRef.current = null
    }
  }, [])

  const startElapsedTimer = useCallback((): void => {
    elapsedTimerRef.current = setInterval(() => {
      setElapsed(pausedElapsedRef.current + Math.floor((Date.now() - startTimeRef.current) / 1000))
    }, 1000)
  }, [])

  const stopElapsedTimer = useCallback((): void => {
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current)
      elapsedTimerRef.current = null
    }
  }, [])

  const getMimeType = useCallback((): string | null => pickRecorderMimeType(), [])

  const handleBoostToggle = useCallback((enabled: boolean): void => {
    setBoostEnabled(enabled)
    localStorage.setItem("lexicanon:boost-enabled", String(enabled))
    boostEnabledRef.current = enabled
    if (compressorRef.current && gainNodeRef.current) {
      applyBoostSettings(compressorRef.current, gainNodeRef.current, enabled)
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recoverActiveRef.current = false
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current)
      if (streamRef.current)
        streamRef.current.getTracks().forEach((t) => {
          t.stop()
        })
      sourceRef.current?.disconnect()
      sourceRef.current = null
      if (audioCtxRef.current) {
        audioCtxRef.current.onstatechange = null
        audioCtxRef.current.close()
      }
      clearInterval(animFrameRef.current)
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {})
        wakeLockRef.current = null
      }
    }
  }, [])

  return {
    streamRef,
    audioCtxRef,
    destinationRef,
    currentVolumeRef,
    startTimeRef,
    pausedElapsedRef,
    monitoring,
    devices,
    selectedDevice,
    setSelectedDevice,
    elapsed,
    setElapsed,
    volume,
    spectrum,
    boostedVolume,
    boostEnabled,
    startMonitoring,
    requestWakeLock,
    releaseWakeLock,
    startElapsedTimer,
    stopElapsedTimer,
    getMimeType,
    handleBoostToggle,
  }
}
